import React, { useEffect, useMemo, useState } from 'react'
import { Loader2, Printer, Search } from 'lucide-react'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { SearchableSelect } from '@/components/ui/searchable-select'
import { useAuthStore } from '@/store/auth.store'
import { attendanceService } from '@/services/attendanceService'
import { employeesService } from '@/services/employeesService'
import { collapseDuplicatedFullName } from '@/lib/employeeName'
import { getLogoPath } from '@/components/shared/Logo'
import type { AttendanceReportRow } from '@/api/types'
import type { DepartmentLookup } from '@/api/types'

function defaultDateRange(): { dateFrom: string; dateTo: string } {
  const now = new Date()
  const y = now.getFullYear()
  const m = String(now.getMonth() + 1).padStart(2, '0')
  const d = String(now.getDate()).padStart(2, '0')
  return { dateFrom: `${y}-${m}-01`, dateTo: `${y}-${m}-${d}` }
}

function formatHours(n?: number | null): string {
  if (n == null || Number.isNaN(Number(n))) return '—'
  const v = Number(n)
  return Number.isInteger(v) ? String(v) : v.toFixed(1)
}

export const AttendanceReport: React.FC = () => {
  const { t } = useTranslation('common')
  const { user } = useAuthStore()
  const branchId = user?.selectedBranch?.branchId
  const defaults = defaultDateRange()

  const [dateFrom, setDateFrom] = useState(defaults.dateFrom)
  const [dateTo, setDateTo] = useState(defaults.dateTo)
  const [departmentId, setDepartmentId] = useState(0)
  const [departments, setDepartments] = useState<DepartmentLookup[]>([])
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(false)
  const [printLoading, setPrintLoading] = useState(false)
  const [list, setList] = useState<AttendanceReportRow[]>([])
  const [totalRecords, setTotalRecords] = useState(0)

  useEffect(() => {
    if (!branchId) {
      setDepartments([])
      return
    }
    let cancelled = false
    employeesService
      .getDepartmentLookups()
      .then((rows) => {
        if (!cancelled && rows) setDepartments(rows)
      })
      .catch(() => {
        if (!cancelled) setDepartments([])
      })
    return () => {
      cancelled = true
    }
  }, [branchId])

  const loadReport = async () => {
    if (!branchId) return
    if (!dateFrom || !dateTo) {
      toast.error(t('attendanceReport.selectDates'))
      return
    }
    setLoading(true)
    try {
      const res = await attendanceService.getReport(branchId, dateFrom, dateTo, departmentId)
      setList(res.data ?? [])
      setTotalRecords(res.totalRecords ?? 0)
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } }
      toast.error(err?.response?.data?.message ?? t('attendanceReport.loadFailed'))
      setList([])
      setTotalRecords(0)
    } finally {
      setLoading(false)
    }
  }

  const filteredList = useMemo(() => {
    if (!search.trim()) return list
    const q = search.toLowerCase()
    return list.filter(
      (r) =>
        (r.employeeName ?? '').toLowerCase().includes(q) ||
        (r.employeeCode ?? '').toLowerCase().includes(q) ||
        (r.department ?? '').toLowerCase().includes(q),
    )
  }, [list, search])

  const totals = useMemo(
    () =>
      filteredList.reduce(
        (acc, r) => ({
          totalDays: acc.totalDays + (r.totalDays || 0),
          totalLates: acc.totalLates + (r.totalLates || 0),
          totalEarlyOut: acc.totalEarlyOut + (r.totalEarlyOut || 0),
          totalAbsents: acc.totalAbsents + (r.totalAbsents || 0),
          totalLeaves: acc.totalLeaves + (r.totalLeaves || 0),
          totalAttendedTime: acc.totalAttendedTime + (r.totalAttendedTime || 0),
        }),
        {
          totalDays: 0,
          totalLates: 0,
          totalEarlyOut: 0,
          totalAbsents: 0,
          totalLeaves: 0,
          totalAttendedTime: 0,
        },
      ),
    [filteredList],
  )

  const handlePrint = async () => {
    if (!branchId) return
    setPrintLoading(true)
    try {
      const res = await attendanceService.getReport(branchId, dateFrom, dateTo, departmentId)
      const data = res.data ?? []
      const branchName = user?.selectedBranch?.branchName ?? 'Branch'
      const dateStr = new Date().toLocaleDateString('en-US', { month: 'numeric', day: '2-digit', year: '2-digit' })
      const timeStr = new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
      const deptLabel =
        departmentId > 0
          ? departments.find((d) => d.id === departmentId)?.lookupNameEn || String(departmentId)
          : t('attendancePage.allDepartments')
      const primaryColor = 'hsl(225, 46%, 16%)'
      const thStyle = `background:${primaryColor};color:white;padding:6px 8px;text-align:center;font-size:11px;font-weight:600;border:1px solid #e5e7eb;`
      const tdStyle = 'padding:6px 8px;font-size:11px;border:1px solid #e5e7eb;text-align:center;'
      const rows = data
        .map(
          (row) => `
        <tr>
          <td style="${tdStyle}">${row.employeeCode || `EMP-${row.empId}`}</td>
          <td style="${tdStyle}">${collapseDuplicatedFullName(row.employeeName) || '—'}</td>
          <td style="${tdStyle}">${row.department || '—'}</td>
          <td style="${tdStyle}">${row.totalDays}</td>
          <td style="${tdStyle}">${row.totalLates}</td>
          <td style="${tdStyle}">${row.totalEarlyOut}</td>
          <td style="${tdStyle}">${row.totalAbsents}</td>
          <td style="${tdStyle}">${row.totalLeaves}</td>
          <td style="${tdStyle}">${formatHours(row.totalAttendedTime)}</td>
        </tr>`,
        )
        .join('')
      const win = window.open('', '_blank')
      if (!win) {
        toast.error(t('attendanceReport.allowPopups'))
        return
      }
      win.document.write(`
        <!DOCTYPE html><html><head>
          <title>${t('attendanceReport.title')}</title>
          <style>
            body { font-family: system-ui, sans-serif; padding: 12px; color: #1f2937; margin: 0; font-size: 12px; }
            table { border-collapse: collapse; width: 100%; }
            .header-row { display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px; }
            .header-date { font-size: 11px; color: #6b7280; }
            .header-branch { flex: 1; text-align: center; font-weight: 600; font-size: 14px; }
            .header-logo img { height: 32px; object-fit: contain; }
            .report-title { text-align: center; font-size: 18px; font-weight: 700; margin: 12px 0 8px; }
            .header-line { height: 3px; background: ${primaryColor}; margin: 8px 0 16px; }
            @media print { body { padding: 0; } }
          </style>
        </head><body>
          <div class="header-row">
            <div class="header-date">${dateStr}, ${timeStr}</div>
            <div class="header-branch">${branchName}</div>
            <div class="header-logo"><img src="${window.location.origin}${getLogoPath()}" alt="Logo" /></div>
          </div>
          <div class="report-title">${t('attendanceReport.title')}</div>
          <div style="text-align:center;font-size:11px;margin-bottom:12px;">${dateFrom} — ${dateTo} · ${deptLabel}</div>
          <div class="header-line"></div>
          <table>
            <thead>
              <tr>
                <th style="${thStyle}">${t('attendanceReport.colEmpCode')}</th>
                <th style="${thStyle}">${t('attendanceReport.colEmployee')}</th>
                <th style="${thStyle}">${t('attendanceReport.colDepartment')}</th>
                <th style="${thStyle}">${t('attendanceReport.colTotalDays')}</th>
                <th style="${thStyle}">${t('attendanceReport.colTotalLates')}</th>
                <th style="${thStyle}">${t('attendanceReport.colTotalEarlyOut')}</th>
                <th style="${thStyle}">${t('attendanceReport.colTotalAbsents')}</th>
                <th style="${thStyle}">${t('attendanceReport.colTotalLeaves')}</th>
                <th style="${thStyle}">${t('attendanceReport.colTotalAttendedTime')}</th>
              </tr>
            </thead>
            <tbody>${rows}</tbody>
          </table>
        </body></html>
      `)
      win.document.close()
      win.focus()
      setTimeout(() => {
        win.print()
        win.close()
      }, 300)
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } }
      toast.error(err?.response?.data?.message ?? t('attendanceReport.loadFailed'))
    } finally {
      setPrintLoading(false)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('attendanceReport.title')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">{t('attendancePage.dateFrom')}</label>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-full" />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">{t('attendancePage.dateTo')}</label>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-full" />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">{t('attendancePage.department')}</label>
            <SearchableSelect
              options={[
                { value: '0', label: t('attendancePage.allDepartments') },
                ...departments
                  .filter((d) => d.id)
                  .map((d) => ({
                    value: String(d.id),
                    label: d.lookupNameEn || d.lookupNameAr || String(d.id),
                  })),
              ]}
              value={departmentId ? String(departmentId) : '0'}
              onValueChange={(v) => setDepartmentId(v ? Number(v) : 0)}
              placeholder={t('attendancePage.allDepartments')}
              searchPlaceholder={t('attendancePage.searchDepartment')}
              emptyMessage={t('attendancePage.emptyDepartment')}
            />
          </div>
          <div className="flex items-end gap-2">
            <Button variant="default" size="sm" onClick={loadReport} disabled={loading || !branchId}>
              {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Search className="mr-2 h-4 w-4" />}
              {t('attendanceReport.search')}
            </Button>
            <Button variant="outline" size="sm" onClick={handlePrint} disabled={printLoading || !branchId}>
              {printLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Printer className="mr-2 h-4 w-4" />}
              {t('attendanceReport.print')}
            </Button>
          </div>
        </div>

        <div className="relative w-64">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder={t('attendanceReport.searchList')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>

        <div className="overflow-x-auto rounded-md border">
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-center">{t('attendanceReport.colEmpCode')}</TableHead>
                  <TableHead className="text-center">{t('attendanceReport.colEmployee')}</TableHead>
                  <TableHead className="text-center">{t('attendanceReport.colDepartment')}</TableHead>
                  <TableHead className="text-center">{t('attendanceReport.colTotalDays')}</TableHead>
                  <TableHead className="text-center">{t('attendanceReport.colTotalLates')}</TableHead>
                  <TableHead className="text-center">{t('attendanceReport.colTotalEarlyOut')}</TableHead>
                  <TableHead className="text-center">{t('attendanceReport.colTotalAbsents')}</TableHead>
                  <TableHead className="text-center">{t('attendanceReport.colTotalLeaves')}</TableHead>
                  <TableHead className="text-center">{t('attendanceReport.colTotalAttendedTime')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredList.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="py-8 text-center text-muted-foreground">
                      {t('attendanceReport.empty')}
                    </TableCell>
                  </TableRow>
                ) : (
                  <>
                    {filteredList.map((row) => (
                      <TableRow key={row.empId}>
                        <TableCell className="text-center">{row.employeeCode || `EMP-${row.empId}`}</TableCell>
                        <TableCell className="text-center">
                          {collapseDuplicatedFullName(row.employeeName) || '—'}
                        </TableCell>
                        <TableCell className="text-center">{row.department || '—'}</TableCell>
                        <TableCell className="text-center">{row.totalDays}</TableCell>
                        <TableCell className="text-center">{row.totalLates}</TableCell>
                        <TableCell className="text-center">{row.totalEarlyOut}</TableCell>
                        <TableCell className="text-center">{row.totalAbsents}</TableCell>
                        <TableCell className="text-center">{row.totalLeaves}</TableCell>
                        <TableCell className="text-center">{formatHours(row.totalAttendedTime)}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell className="text-center font-semibold" colSpan={3}>
                        {t('attendanceReport.totals')}
                      </TableCell>
                      <TableCell className="text-center font-semibold">{totals.totalDays}</TableCell>
                      <TableCell className="text-center font-semibold">{totals.totalLates}</TableCell>
                      <TableCell className="text-center font-semibold">{totals.totalEarlyOut}</TableCell>
                      <TableCell className="text-center font-semibold">{totals.totalAbsents}</TableCell>
                      <TableCell className="text-center font-semibold">{totals.totalLeaves}</TableCell>
                      <TableCell className="text-center font-semibold">
                        {formatHours(totals.totalAttendedTime)}
                      </TableCell>
                    </TableRow>
                  </>
                )}
              </TableBody>
            </Table>
          )}
        </div>
        {totalRecords > 0 && (
          <p className="text-sm text-muted-foreground">
            {t('attendanceReport.showing', { shown: filteredList.length, total: totalRecords })}
          </p>
        )}
      </CardContent>
    </Card>
  )
}
